import { Phone, ArrowRight } from "lucide-react";
import { contactInfo } from "@/data/machines";
import { brand } from "@/data/brand";
import Reveal from "@/components/site/Reveal";

export default function Hero() {
  return (
    <section
      id="inicio"
      data-testid="hero-section"
      className="relative pt-24 sm:pt-36 md:pt-36 pb-20 md:pb-28 overflow-hidden bg-slate-50 ambient-light"
    >
      {/* Ambient light blobs */}
      <div aria-hidden className="absolute -top-24 -left-24 w-[26rem] h-[26rem] rounded-full bg-sky-400/10 blur-3xl pointer-events-none" />
      <div aria-hidden className="absolute -bottom-32 right-0 w-[24rem] h-[24rem] rounded-full bg-sky-300/10 blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* Text */}
          <div className="lg:col-span-6 order-2 lg:order-1">
            <Reveal>
              <span
                data-testid="hero-eyebrow"
                className="inline-flex items-center gap-2 bg-sky-500/10 text-sky-700 text-xs font-semibold tracking-wide uppercase px-3 py-1.5 rounded-full border border-sky-500/20"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-sky-500 animate-soft-pulse" />
                Alquiler profesional · Santa Olalla (Toledo)
              </span>
            </Reveal>

            <Reveal delay={0.08}>
              <h1
                data-testid="hero-title"
                className="font-heading mt-6 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-950 leading-[1.03]"
              >
                Alquiler de maquinaria{" "}
                <span className="text-sky-600">profesional</span>
              </h1>
            </Reveal>

            <Reveal delay={0.16}>
              <p
                data-testid="hero-subtitle"
                className="mt-6 text-base md:text-lg text-slate-600 max-w-xl leading-relaxed"
              >
                Disponemos de un amplio catálogo de maquinaria. Consúltanos su disponibilidad.
              </p>
            </Reveal>

            <Reveal delay={0.24}>
              <div className="mt-9 flex flex-col sm:flex-row gap-3">
                <a
                  href="#catalogo"
                  data-testid="hero-cta-catalog"
                  className="group shine inline-flex items-center justify-center gap-2 bg-sky-500 hover:bg-sky-600 text-white font-semibold rounded-xl px-6 py-3.5 transition-all duration-300 shadow-md shadow-sky-500/20 hover:shadow-lg hover:shadow-sky-500/30 hover:-translate-y-0.5"
                >
                  Ver maquinaria
                  <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                </a>
                <a
                  href={`tel:${contactInfo.phoneRaw}`}
                  data-testid="hero-cta-call"
                  className="group inline-flex items-center justify-center gap-2 bg-slate-950 hover:bg-slate-800 text-white font-semibold rounded-xl px-6 py-3.5 transition-all duration-300 hover:-translate-y-0.5 shadow-sm hover:shadow-md"
                >
                  <Phone className="w-4 h-4 group-hover:wiggle" /> Llamar ahora
                </a>
              </div>
            </Reveal>

            <Reveal delay={0.32}>
              <div className="mt-12 max-w-md" />
            </Reveal>
          </div>

          {/* Image (natural aspect ratio, no cropping) */}
          <Reveal delay={0.1} y={30} className="lg:col-span-6 order-1 lg:order-2 relative">
            <div className="relative rounded-3xl overflow-hidden shadow-[0_30px_80px_-20px_rgba(2,32,71,0.35)] ring-1 ring-slate-200/70 bg-slate-100">
              <img
                src="https://customer-assets.emergentagent.com/job_build-fleet-3/artifacts/g6lhqv5r_628861B0-EE72-4FB7-935B-D30B605D8CB8.png"
                alt="Alquiler de maquinaria en Toledo — Maquinaria Santacruz, Santa Olalla"
                className="w-full h-auto block object-contain transition-transform duration-[1400ms] ease-out hover:scale-[1.02]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/20 via-transparent to-transparent pointer-events-none" />
              {/* Logo watermark badge */}
              <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-md rounded-xl shadow-lg shadow-slate-900/10 px-3 py-2 flex items-center gap-2 border border-white/60 animate-floaty">
                <img src={brand.logoUrl} alt="Logo de Maquinaria Santacruz" className="h-8 w-8 rounded-md object-cover" />
                <div className="leading-tight">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Venta y alquiler</div>
                  <div className="text-xs font-bold text-slate-900">Maquinaria Santacruz</div>
                </div>
              </div>
            </div>

            {/* Floating phone card */}
            <div className="hidden md:flex absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-2xl shadow-slate-900/10 border border-slate-100 p-4 gap-3 items-center group hover:-translate-y-0.5 transition-transform duration-300">
              <div className="w-11 h-11 rounded-xl bg-sky-500 flex items-center justify-center shadow-md shadow-sky-500/30">
                <Phone className="w-5 h-5 text-white group-hover:wiggle" />
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">Llámanos ahora</div>
                <a href={`tel:${contactInfo.phoneRaw}`} className="block font-heading font-bold text-slate-900 hover:text-sky-600 transition-colors">{contactInfo.phone}</a>
                <span className="block font-heading font-bold text-slate-900 leading-tight">{contactInfo.phone2}</span>
              </div>
              <span className="absolute -inset-px rounded-2xl ring-1 ring-sky-500/0 group-hover:ring-sky-500/20 transition duration-300 pointer-events-none" />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
